import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Truck, Search } from 'lucide-react';
import Card from '../../components/Card';
import Input from '../../components/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, coalesce, renderCellDate } from '../../utils/helpers';

const VehicleEquipmentView = () => {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { error: showError } = useToast();

  useEffect(() => {
    loadEquipment();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [equipment, searchQuery]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      // Fetch only data for this client
      const data = await apiService.getVehicleEquipment(user?.id, null);
      setEquipment(data);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = equipment;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.vehicleRegistration?.toLowerCase().includes(query) ||
          item.serialNumber?.toLowerCase().includes(query) ||
          item.affectation?.toLowerCase().includes(query)
      );
    }

    setFilteredEquipment(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Truck className="w-8 h-8 text-red-600" />
          Équipements Véhicules
        </h1>
        <p className="text-gray-600 mt-1">
          Consultation des extincteurs installés dans les véhicules
        </p>
      </div>

      {/* Search */}
      <Card>
        <Input
          placeholder="Rechercher par matricule, N° série, affectation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </Card>

      {/* Table Structure - Read Only */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">N°</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Structure</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Affectation</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Type du véhicule</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Matricule du véhicule</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Type de l'extincteur</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Date de contrôle</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Prochain contrôle</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Date de récupération</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Récupéré par</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEquipment.map((item, index) => {
                return (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-3 text-sm text-center text-gray-900">{index + 1}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{renderCell(item.structure)}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{renderCell(item.affectation)}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">{renderCell(item.vehicleType)}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">{renderCell(item.vehicleRegistration)}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {renderCell(coalesce(item.detailedType, item.extinguisherType))}
                    </td>
                    <td className="px-3 py-3 text-sm text-center text-gray-700">
                      {renderCellDate(item.lastMaintenanceDate)}
                    </td>
                    <td className="px-3 py-3 text-sm text-center text-gray-700">
                      {renderCellDate(item.nextMaintenanceDate)}
                    </td>
                    <td className="px-3 py-3 text-sm text-center text-gray-700">
                      {renderCellDate(item.recoveryDate)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">{renderCell(item.recoveredBy)}</td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>

          {filteredEquipment.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucun équipement de véhicule trouvé
              </h3>
              <p className="text-gray-600">
                Aucun extincteur de véhicule n'a encore été enregistré
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">{filteredEquipment.length}</p>
            <p className="text-sm text-gray-600 mt-1">Total Véhicules</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {filteredEquipment.filter(e => e.lastMaintenanceDate).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Contrôlés</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-600">
              {filteredEquipment.filter(e => !e.lastMaintenanceDate).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">À contrôler</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default VehicleEquipmentView;

